import { useState, useMemo } from 'react'
import { Plus, Trash2, MessageSquare, Settings, PanelLeftClose, PanelLeft, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { ConversationSummary } from '@/types/api'

interface ConversationSidebarProps {
  open: boolean
  conversations: ConversationSummary[]
  activeConversationId: string | null
  onNewChat: () => void
  onSelectConversation: (id: string) => void
  onDeleteConversation: (id: string) => void
  onToggle: () => void
  onManageAgents: () => void
  username?: string
}

function groupConversations(conversations: ConversationSummary[]) {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const sevenDaysAgo = new Date(today)
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

  const groups: { label: string; items: ConversationSummary[] }[] = [
    { label: 'Today', items: [] },
    { label: 'Yesterday', items: [] },
    { label: 'Previous 7 Days', items: [] },
    { label: 'Older', items: [] },
  ]

  for (const conv of conversations) {
    const updated = new Date(conv.updated_at)
    if (updated >= today) {
      groups[0]!.items.push(conv)
    } else if (updated >= yesterday) {
      groups[1]!.items.push(conv)
    } else if (updated >= sevenDaysAgo) {
      groups[2]!.items.push(conv)
    } else {
      groups[3]!.items.push(conv)
    }
  }

  return groups.filter((g) => g.items.length > 0)
}

export function ConversationSidebar({
  open,
  conversations,
  activeConversationId,
  onNewChat,
  onSelectConversation,
  onDeleteConversation,
  onToggle,
  onManageAgents,
  username,
}: ConversationSidebarProps) {
  const [search, setSearch] = useState('')
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const filtered = useMemo(() => {
    if (!search.trim()) return conversations
    const q = search.toLowerCase()
    return conversations.filter((c) => c.title.toLowerCase().includes(q))
  }, [conversations, search])

  const groups = useMemo(() => groupConversations(filtered), [filtered])

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    setDeletingId(id)
    try {
      await Promise.resolve(onDeleteConversation(id))
    } finally {
      setDeletingId(null)
    }
  }

  if (!open) {
    return (
      <div className="flex flex-col items-center py-3 px-2 border-r border-border bg-card w-12 flex-shrink-0">
        <button
          onClick={onToggle}
          className="p-1.5 rounded-md hover:bg-accent transition-colors text-muted-foreground"
          title="Open sidebar"
        >
          <PanelLeft className="h-4 w-4" />
        </button>
        <button
          onClick={onNewChat}
          className="mt-2 p-1.5 rounded-md hover:bg-accent transition-colors text-muted-foreground"
          title="New chat"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col w-64 flex-shrink-0 border-r border-border bg-card h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-border">
        <span className="text-sm font-semibold text-foreground">Conversations</span>
        <button
          onClick={onToggle}
          className="p-1 rounded-md hover:bg-accent transition-colors text-muted-foreground"
          title="Close sidebar"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      {/* New Chat button */}
      <div className="px-3 pt-3 pb-2">
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2 h-8"
          onClick={onNewChat}
        >
          <Plus className="h-3.5 w-3.5" />
          New Chat
        </Button>
      </div>

      {/* Search */}
      <div className="px-3 pb-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-7 pr-2 py-1.5 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto px-2">
        {groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <MessageSquare className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-xs text-muted-foreground">
              {search ? 'No conversations match your search' : 'No conversations yet. Start a new chat!'}
            </p>
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.label} className="mb-3">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1">
                {group.label}
              </p>
              {group.items.map((conv) => (
                <div
                  key={conv.id}
                  className={`relative group flex items-center rounded-md px-2 py-1.5 cursor-pointer transition-colors mb-0.5 ${
                    conv.id === activeConversationId
                      ? 'bg-accent text-accent-foreground'
                      : 'hover:bg-accent/50 text-foreground'
                  }`}
                  onClick={() => onSelectConversation(conv.id)}
                  onMouseEnter={() => setHoveredId(conv.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <span className="text-xs truncate flex-1 pr-1">{conv.title}</span>
                  {(hoveredId === conv.id || conv.id === activeConversationId) && (
                    <button
                      onClick={(e) => handleDelete(e, conv.id)}
                      disabled={deletingId === conv.id}
                      className="flex-shrink-0 p-0.5 rounded hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                      title="Delete conversation"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-border px-3 py-2 space-y-1">
        <button
          onClick={onManageAgents}
          className="flex items-center gap-2 w-full px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded-md transition-colors"
        >
          <Settings className="h-3.5 w-3.5" />
          Manage Agents
        </button>
        {username && (
          <div className="flex items-center gap-2 px-2 py-1">
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-semibold text-primary flex-shrink-0">
              {username.charAt(0).toUpperCase()}
            </div>
            <span className="text-xs text-muted-foreground truncate">{username}</span>
          </div>
        )}
      </div>
    </div>
  )
}
